import { z } from 'zod';
import type { ShapeManifest, ShapePack } from './shape-manifest.types';
import type { ConnectorManifest, ConnectorPack } from './connector-manifest.types';
import type { RelationshipCategoryManifest, RelationshipOntologyMapping, RelationshipPack, RelationshipSubtypeManifest } from './relationship-manifest.types';
import { GenogramPersonManifest } from '../../domains/genogram/shapes/person.manifest';
import { CommonTextManifest } from '../../domains/genogram/shapes/text.manifest';
import { CommonStickyNoteManifest } from '../../domains/genogram/shapes/stickyNote.manifest';
import { BasicShapeManifests } from '../../domains/diagram/shapes/basic/basicShapes.manifest';
import { FlowchartShapeManifests } from '../../domains/process/shapes/flowchart/flowchartShapes.manifest';
import { BpmnShapeManifests } from '../../domains/process/shapes/bpmn/bpmnShapes.manifest';
import { SequenceFlowConnectorManifest, MessageFlowConnectorManifest, ExceptionFlowConnectorManifest, DataAssociationConnectorManifest } from '../../domains/process/connectors';
import { CommonFreehandManifest } from '../../domains/genogram/shapes/freehand.manifest';
import {
  LineConnectorManifest,
  MarriageConnectorManifest,
  ChildConnectorManifest,
  SiblingConnectorManifest,
  HarmonyConnectorManifest,
  DistrustConnectorManifest,
  CloseHostileConnectorManifest,
  FusedHostileConnectorManifest,
  DistanceHostileConnectorManifest,
  CutoffRepairedConnectorManifest,
  EmotionalConnectionConnectorManifest,
  NeverMetConnectorManifest,
  SeparationConnectorManifest,
  EngagementConnectorManifest,
  CohabitationConnectorManifest,
  InLoveHarmonyConnectorManifest,
  HalfSiblingConnectorManifest,
  TwinMonoConnectorManifest,
  TwinDiConnectorManifest,
} from '../../domains/genogram/connectors/manifests.ts';
import { TableShapeManifest } from '../../domains/diagram/shapes/table/table.manifest';
import { ImageShapeManifest } from '../../domains/diagram/shapes/image.manifest';
import { PdfShapeManifest } from '../../domains/diagram/shapes/pdf.manifest';

// Diagram type registry
export type DiagramType = 'genogram' | 'flowchart' | 'mindmap' | 'network' | 'org' | 'database' | 'uml' | 'planning' | 'software-design' | 'generic';

// The registry intentionally stores heterogeneous manifest generics. Runtime
// schema parsing keeps the boundary safe while callers recover concrete types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RegisteredShapeTypeDefinition = ShapeTypeDefinition<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RegisteredConnectorTypeDefinition = ConnectorTypeDefinition<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RegisteredShapeManifest = ShapeManifest<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RegisteredConnectorManifest = ConnectorManifest<any>;

export interface ShapeTypeDefinition<T> {
  type: string;
  propsSchema: z.ZodType<T>;
  createDefaultProps: () => T;
}

export interface ConnectorTypeDefinition<T> {
  type: string;
  propsSchema: z.ZodType<T>;
  createDefaultProps: () => T;
}

export interface DiagramTypeConfig {
  shapes: Record<string, RegisteredShapeTypeDefinition>;
  connectors: Record<string, RegisteredConnectorTypeDefinition>;
  // New manifest-driven registration (by canonical shape id)
  manifests?: Record<string, RegisteredShapeManifest>;
  connectorManifests?: Record<string, RegisteredConnectorManifest>;
  relationshipCategories?: Record<string, RelationshipCategoryManifest>;
  relationshipSubtypes?: Record<string, RelationshipSubtypeManifest>;
}

const genogramPersonProps = z.object({
  name: z.string().optional(),
  sex: z.union([z.literal('male'), z.literal('female'), z.literal('unknown')]).optional(),
  birthYear: z.number().optional(),
  deathYear: z.number().optional(),
  location: z.string().optional(),
  fillColor: z.string().optional(),
  attrs: z.record(z.string(), z.any()).optional(),
});

const marriageProps = z.object({
  marriageYear: z.number().optional(),
  divorced: z.boolean().optional(),
});

const childProps = z.object({});
const siblingProps = z.object({});

// Generic simple line connector used by canvas default connector
const lineConnectorProps = z.object({
  color: z.string().optional(),
  width: z.number().optional(),
  dashed: z.boolean().optional(),
  dash: z.array(z.number()).optional(),
  startArrow: z.object({ type: z.enum(['none','triangle','circle','circleOutline','diamond','bar','outlineBlock','backslash','chevron']).default('none'), size: z.number().default(6) }).optional(),
  endArrow: z.object({ type: z.enum(['none','triangle','circle','circleOutline','diamond','bar','outlineBlock','backslash','chevron']).default('triangle'), size: z.number().default(8) }).optional(),
  tension: z.number().optional(),
  routingType: z.enum(['straight','orthogonal','orthogonalRounded','bezier']).optional(),
  fromAnchor: z.enum(['top', 'right', 'bottom', 'left']).optional(),
  toAnchor: z.enum(['top', 'right', 'bottom', 'left']).optional(),
  fromOffset: z.number().min(0).max(1).optional(),
  toOffset: z.number().min(0).max(1).optional(),
  label: z.string().optional(),
  labelOffset: z.number().min(0).max(1).optional(),
});

// Freehand path (rendered as Konva.Line)
const freehandProps = z.object({
  points: z.array(z.number()),
  strokeColor: z.string().optional(),
  strokeWidth: z.number().optional(),
  opacity: z.number().optional(),
  lineCap: z.string().optional(),
  lineJoin: z.string().optional(),
  tension: z.number().optional(),
});

// Simple text shape props
const textShapeProps = z.object({
  text: z.string().optional(),
  fontSize: z.number().optional(),
  fontFamily: z.string().optional(),
  fillColor: z.string().optional(),
  strokeColor: z.string().optional(),
  attrs: z.record(z.string(), z.any()).optional(),
});

const manifestsToMap = (manifests: Array<RegisteredShapeManifest>) =>
  manifests.reduce<Record<string, RegisteredShapeManifest>>((acc, manifest) => {
    acc[manifest.id] = manifest;
    return acc;
  }, {});

const CommonShapeManifests: Record<string, RegisteredShapeManifest> = {
  ...manifestsToMap(BasicShapeManifests),
  text: CommonTextManifest,
  'sticky-note': CommonStickyNoteManifest,
  freehand: CommonFreehandManifest,
  image: ImageShapeManifest,
  pdf: PdfShapeManifest,
};

const resolveShapeManifest = (
  diagramType: DiagramType,
  shapeId: string,
): RegisteredShapeManifest | undefined => {
  return DiagramTypeRegistry[diagramType]?.manifests?.[shapeId] ?? CommonShapeManifests[shapeId];
};

export const DiagramTypeRegistry: Record<DiagramType, DiagramTypeConfig> = {
  genogram: {
    shapes: {
      person: {
        type: 'person',
        propsSchema: genogramPersonProps,
        createDefaultProps: () => ({ name: 'New Person', sex: 'unknown' }),
      },
      text: {
        type: 'text',
        propsSchema: textShapeProps,
        createDefaultProps: () => ({ text: 'Text', fontSize: 16, fontFamily: 'Arial' }),
      },
      freehand: {
        type: 'freehand',
        propsSchema: freehandProps,
        createDefaultProps: () => ({ points: [] }),
      },
    },
    connectors: {
      line: {
        type: 'line',
        propsSchema: lineConnectorProps,
        createDefaultProps: () => ({ width: 2, color: '#334155', dashed: false, endArrow: { type: 'triangle', size: 8 } }),
      },
      marriage: {
        type: 'marriage',
        propsSchema: marriageProps,
        createDefaultProps: () => ({ divorced: false }),
      },
      child: {
        type: 'child',
        propsSchema: childProps,
        createDefaultProps: () => ({}),
      },
      sibling: {
        type: 'sibling',
        propsSchema: siblingProps,
        createDefaultProps: () => ({}),
      },
    },
    manifests: {
      ...CommonShapeManifests,
      ...manifestsToMap(FlowchartShapeManifests),
      table: TableShapeManifest,
      person: GenogramPersonManifest,
    },
    connectorManifests: {
      line: LineConnectorManifest,
      marriage: MarriageConnectorManifest,
      child: ChildConnectorManifest,
      sibling: SiblingConnectorManifest,
      harmony: HarmonyConnectorManifest,
      in_love_harmony: InLoveHarmonyConnectorManifest,
      distrust: DistrustConnectorManifest,
      close_hostile: CloseHostileConnectorManifest,
      fused_hostile_combined: FusedHostileConnectorManifest,
      distance_hostile: DistanceHostileConnectorManifest,
      cutoff_repaired: CutoffRepairedConnectorManifest,
      emotional_connection: EmotionalConnectionConnectorManifest,
      never_met: NeverMetConnectorManifest,
      separation: SeparationConnectorManifest,
      engagement: EngagementConnectorManifest,
      cohabitation: CohabitationConnectorManifest,
      'half-siblings': HalfSiblingConnectorManifest,
      'twin-mono': TwinMonoConnectorManifest,
      'twin-di': TwinDiConnectorManifest,
    },
    relationshipCategories: {},
    relationshipSubtypes: {}
  },
  flowchart: {
    shapes: {},
    connectors: {},
    manifests: {
      ...CommonShapeManifests,
      ...manifestsToMap(FlowchartShapeManifests),
      ...manifestsToMap(BpmnShapeManifests),
      table: TableShapeManifest,
    },
    // Lazy getters: this module and flowchartConnectors.pack.ts import each
    // other (pack → registerConnector, here → the manifests). Eager
    // references TDZ-crash IIFE viewer bundles when the bundler evaluates
    // this module first ("Cannot access 'SequenceFlowConnectorManifest'
    // before initialization"). Getters defer the read until after init.
    connectorManifests: {
      get sequence_flow() { return SequenceFlowConnectorManifest; },
      get message_flow() { return MessageFlowConnectorManifest; },
      get exception_flow() { return ExceptionFlowConnectorManifest; },
      get data_association() { return DataAssociationConnectorManifest; },
    },
    relationshipCategories: {},
    relationshipSubtypes: {}
  },
  mindmap: { shapes: {}, connectors: {}, manifests: {}, connectorManifests: {}, relationshipCategories: {}, relationshipSubtypes: {} },
  org: {
    shapes: {},
    connectors: {},
    manifests: {},
    connectorManifests: {},
    relationshipCategories: {},
    relationshipSubtypes: {},
  },
  database: { shapes: {}, connectors: {}, manifests: {}, connectorManifests: {}, relationshipCategories: {}, relationshipSubtypes: {} },
  uml: { shapes: {}, connectors: {}, manifests: {}, connectorManifests: {}, relationshipCategories: {}, relationshipSubtypes: {} },
  planning: { shapes: {}, connectors: {}, manifests: {}, connectorManifests: {}, relationshipCategories: {}, relationshipSubtypes: {} },
  'software-design': { shapes: {}, connectors: {}, manifests: {}, connectorManifests: {}, relationshipCategories: {}, relationshipSubtypes: {} },
  network: { shapes: {}, connectors: {}, manifests: {}, connectorManifests: {}, relationshipCategories: {}, relationshipSubtypes: {} },
  generic: { shapes: {}, connectors: {}, manifests: {}, connectorManifests: {}, relationshipCategories: {}, relationshipSubtypes: {} },
};

export function validateShapeProps(
  diagramType: DiagramType,
  shapeType: string,
  props: unknown
) {
  // Prefer manifest schema if available, else fall back to legacy type definition
  const manifest = resolveShapeManifest(diagramType, shapeType);
  if (manifest) {
    return manifest.propsSchema.parse(props);
  }
  const cfg = DiagramTypeRegistry[diagramType]?.shapes[shapeType];
  if (!cfg) throw new Error(`Unknown shape type ${shapeType} for ${diagramType}`);
  return cfg.propsSchema.parse(props);
}

export function validateConnectorProps(
  diagramType: DiagramType,
  connectorType: string,
  props: unknown
) {
  // Prefer manifest schema if available, else fall back to legacy type definition
  const manifest = DiagramTypeRegistry[diagramType]?.connectorManifests?.[connectorType];
  if (manifest) {
    return manifest.propsSchema.parse(props);
  }
  const cfg = DiagramTypeRegistry[diagramType]?.connectors[connectorType];
  if (!cfg) throw new Error(`Unknown connector type ${connectorType} for ${diagramType}`);
  return cfg.propsSchema.parse(props);
}

// Manifest registration API
export function registerShape(diagramType: DiagramType, manifest: RegisteredShapeManifest) {
  const cfg = DiagramTypeRegistry[diagramType];
  if (!cfg) throw new Error(`Unknown diagram type: ${diagramType}`);
  cfg.manifests = cfg.manifests || {};
  cfg.manifests[manifest.id] = manifest;
}

export function registerShapePack(pack: ShapePack) {
  const { diagramType, shapes } = pack;
  shapes.forEach((m) => registerShape(diagramType, m));
}

export function getShapeManifest(diagramType: DiagramType, shapeId: string): RegisteredShapeManifest | undefined {
  return resolveShapeManifest(diagramType, shapeId);
}

// Connector manifest registration API
export function registerConnector(diagramType: DiagramType, manifest: RegisteredConnectorManifest) {
  const cfg = DiagramTypeRegistry[diagramType];
  if (!cfg) throw new Error(`Unknown diagram type: ${diagramType}`);
  cfg.connectorManifests = cfg.connectorManifests || {};
  // Seeded entries are lazy getters (see the flowchart entry above), and a
  // getter-only property throws on plain assignment under ESM strict mode.
  // Define instead of assign so registration replaces the accessor with the
  // resolved manifest rather than crashing the domain's plugin registration.
  Object.defineProperty(cfg.connectorManifests, manifest.id, {
    value: manifest,
    writable: true,
    enumerable: true,
    configurable: true,
  });
}

export function registerConnectorPack(pack: ConnectorPack) {
  const { diagramType, connectors } = pack;
  connectors.forEach((m) => registerConnector(diagramType, m));
}

export function getConnectorManifest(diagramType: DiagramType, connectorId: string): RegisteredConnectorManifest | undefined {
  return DiagramTypeRegistry[diagramType]?.connectorManifests?.[connectorId];
}

export function listConnectorManifests(diagramType: DiagramType): Array<RegisteredConnectorManifest> {
  const cfg = DiagramTypeRegistry[diagramType];
  if (!cfg || !cfg.connectorManifests) return [];
  return Object.values(cfg.connectorManifests);
}

// Relationship registration API (domain-agnostic)
export function registerRelationshipPack(pack: RelationshipPack) {
  const { diagramType, categories, subtypes } = pack;
  const cfg = DiagramTypeRegistry[diagramType];
  if (!cfg) throw new Error(`Unknown diagram type: ${diagramType}`);
  cfg.relationshipCategories = cfg.relationshipCategories || {};
  cfg.relationshipSubtypes = cfg.relationshipSubtypes || {};
  categories.forEach((c) => {
    cfg.relationshipCategories![c.id] = c;
  });
  subtypes.forEach((s) => {
    cfg.relationshipSubtypes![s.id] = s;
  });
}

export function listRelationshipCategories(diagramType: DiagramType): Array<RelationshipCategoryManifest> {
  const cfg = DiagramTypeRegistry[diagramType];
  if (!cfg || !cfg.relationshipCategories) return [];
  return Object.values(cfg.relationshipCategories).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export function listRelationshipSubtypes(diagramType: DiagramType, categoryId: string): Array<RelationshipSubtypeManifest> {
  const cfg = DiagramTypeRegistry[diagramType];
  if (!cfg || !cfg.relationshipSubtypes) return [];
  return Object.values(cfg.relationshipSubtypes).filter(s => s.categoryId === categoryId);
}

export function listAllRelationshipSubtypes(diagramType: DiagramType): Array<RelationshipSubtypeManifest> {
  const cfg = DiagramTypeRegistry[diagramType];
  if (!cfg || !cfg.relationshipSubtypes) return [];
  return Object.values(cfg.relationshipSubtypes);
}

export function relationshipSubtypeAppliesToScope(
  subtype: RelationshipSubtypeManifest,
  scope?: string,
): boolean {
  const normalizedScope = scope?.trim();
  if (!normalizedScope) return true;
  if (!subtype.appliesTo?.length) return true;
  return subtype.appliesTo.includes(normalizedScope);
}

export function listRelationshipSubtypesForScope(
  diagramType: DiagramType,
  categoryId: string,
  scope?: string,
): Array<RelationshipSubtypeManifest> {
  return listRelationshipSubtypes(diagramType, categoryId).filter((subtype) => (
    relationshipSubtypeAppliesToScope(subtype, scope)
  ));
}

export function listAllRelationshipSubtypesForScope(
  diagramType: DiagramType,
  scope?: string,
): Array<RelationshipSubtypeManifest> {
  return listAllRelationshipSubtypes(diagramType).filter((subtype) => (
    relationshipSubtypeAppliesToScope(subtype, scope)
  ));
}

export function getRelationshipSubtype(diagramType: DiagramType, subtypeId: string): RelationshipSubtypeManifest | undefined {
  const cfg = DiagramTypeRegistry[diagramType];
  return cfg?.relationshipSubtypes?.[subtypeId];
}

export function findRelationshipSubtypeByPredicate(
  diagramType: DiagramType,
  predicate: string,
): RelationshipSubtypeManifest | undefined {
  return listAllRelationshipSubtypes(diagramType).find((subtype) => (
    subtype.ontology?.predicate === predicate
  ));
}

export function getRelationshipMapping(
  diagramType: DiagramType,
  subtypeId: string,
): { edgeType: string; defaultEdgeProps: Record<string, unknown>; ontology?: RelationshipOntologyMapping } | null {
  const sub = getRelationshipSubtype(diagramType, subtypeId);
  if (!sub) return null;
  return {
    edgeType: sub.edgeType || 'line',
    defaultEdgeProps: sub.defaultEdgeProps || {},
    ...(sub.ontology ? { ontology: sub.ontology } : {}),
  };
}

// Flags that are mutually exclusive within an edge type — used for reverse-lookup disambiguation
export const RELATIONSHIP_EXCLUSIVE_FLAGS: Record<string, Array<string>> = {
  marriage: ['divorced', 'separated', 'engaged', 'house', 'dashDot', 'dashDoubleDot', 'dashSingleDot', 'separatedInFact', 'solidDashedDouble', 'cohabSeparation', 'nonSentimentalCohabSep', 'nonSentimentalCohab', 'temporaryRelation', 'legalCohabLegalSep', 'casualRelSep', 'separationInFactSlash', 'legalSeparationSlash', 'nullity', 'divorcedV2'],
  child: ['adopted', 'guardianship', 'step'],
  sibling: ['half', 'step'],
  line: ['loveAffair', 'cutoffRepaired', 'neverMet', 'bestFriends', 'emotionalConnection', 'inLoveHarmony', 'distrust', 'fusedHostile', 'distanceHostile', 'closeHostile', 'distantViolence', 'closeViolence', 'focusedNegatively', 'abusePattern', 'physicalAbuse', 'emotionalAbuse', 'sexualAbuse', 'neglectAbuse', 'manipulative', 'controlling', 'jealous', 'fanAdmirer', 'limerence', 'plainNormal'],
};

export function propsEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ka = Object.keys(a as Record<string, unknown>);
    const kb = Object.keys(b as Record<string, unknown>);
    if (ka.length !== kb.length) return false;
    const ar = a as Record<string, unknown>;
    const br = b as Record<string, unknown>;
    return ka.every(k => ar[k] === br[k]);
  }
  return false;
}

/**
 * Reverse-lookup: find the relationship subtype manifest that matches a given edge's type and attrs.
 * Uses the same matching logic as RelationshipSubtypeSwitcher.isActiveSubtype().
 */
export function findSubtypeForEdge(
  diagramType: DiagramType,
  edgeType: string,
  attrs: Record<string, unknown>
): RelationshipSubtypeManifest | undefined {
  const cfg = DiagramTypeRegistry[diagramType];
  if (!cfg?.relationshipSubtypes) return undefined;
  const subtypes = Object.values(cfg.relationshipSubtypes);

  for (const sub of subtypes) {
    if ((sub.edgeType || 'line') !== edgeType) continue;
    const expected = sub.defaultEdgeProps || {};

    let match = true;
    for (const [k, v] of Object.entries(expected)) {
      if (!propsEqual(attrs[k], v)) { match = false; break; }
    }
    if (!match) continue;

    const exclusives = RELATIONSHIP_EXCLUSIVE_FLAGS[sub.edgeType || ''] || [];
    let exclusive = true;
    for (const f of exclusives) {
      const subtypeExpectsFlag = Object.prototype.hasOwnProperty.call(expected, f);
      const edgeHasFlag = Boolean(attrs[f]);
      if (!subtypeExpectsFlag && edgeHasFlag) { exclusive = false; break; }
    }
    if (!exclusive) continue;

    return sub;
  }
  return undefined;
}
